import { AlertTriangle, CreditCard } from 'lucide-react';

import { PageBanner } from '@/components/core/common/banners/PageBanner';
import {
  useBillingBanner,
  type BillingBannerState,
} from '@/hooks/account/useBillingBanner';

/**
 * The dunning strip — ROUTE_INVENTORY Part 1, G9. `role='alert'`.
 *
 *   - **Action required** (tone: warning) — the renewal needs an SCA / OTP
 *     challenge on Stripe's hosted invoice, so the action LEAVES the app: an
 *     external link, new tab, `noopener`. Nothing has failed yet.
 *   - **Past due** (tone: destructive) — the charge was declined; the action is
 *     an in-app link to `/settings?tab=payment`, where a new card goes.
 *
 * Getting those two the wrong way round sends a user to a settings page that
 * cannot complete an SCA challenge. An action-required subscription with no
 * hosted invoice URL falls back to the settings link rather than a dead
 * button.
 *
 * The action is the shared outline button, like every page-wide strip. It was
 * a solid amber CTA (white on `amber-600` at first, 3.19:1, then `amber-700`),
 * the only strip whose action looked different from the others.
 *
 * `BillingActionBannerView` is the strip; `BillingActionBanner` wires it to
 * `useBillingBanner`, standalone. In the app it is shown by
 * `features/shell/ShellBanners`, below every other strip in priority.
 */
export function BillingActionBannerView({
  kind,
  hostedUrl,
  onDismiss,
}: {
  kind: NonNullable<BillingBannerState['kind']>;
  hostedUrl: string | null;
  onDismiss: () => void;
}) {
  const actionRequired = kind === 'action-required';
  return (
    <PageBanner
      testId='billing-action-banner'
      tone={actionRequired ? 'warning' : 'destructive'}
      icon={actionRequired ? CreditCard : AlertTriangle}
      message={
        actionRequired
          ? 'Your payment needs confirmation.'
          : "Your last payment didn't go through."
      }
      detail={
        actionRequired
          ? 'Confirm it to keep your plan active.'
          : 'Update your card to avoid losing access.'
      }
      action={
        actionRequired && hostedUrl
          ? { label: 'Confirm payment', href: hostedUrl }
          : { label: 'Update payment method', to: '/settings?tab=payment' }
      }
      onDismiss={onDismiss}
      dismissLabel='Dismiss billing banner'
      live='alert'
    />
  );
}

export function BillingActionBanner() {
  const { kind, hostedUrl, dismissed, dismiss } = useBillingBanner();
  if (!kind || dismissed) return null;
  return (
    <BillingActionBannerView
      kind={kind}
      hostedUrl={hostedUrl}
      onDismiss={dismiss}
    />
  );
}
